import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { JsonViewer } from '@/components/JsonViewer';
import { MetadataItem } from '@/components/MetadataItem';
import { userLimitsState, userRolesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { formatDateTime, formatRelative } from '@/util/dates';
import { USER_LIMIT_DEFAULTS, type User, UserLimitKeySchema } from '@rock/types';
import { todayLocalDate } from '@rock/util';
import type { FC } from 'react';

const PROVIDER_LABELS: Record<string, string> = {
    'google.com': 'Google',
    'apple.com': 'Apple',
    password: 'Password',
    phone: 'Phone',
};

export const UserOverviewTab: FC<{ user: User }> = ({ user }) => (
    <div className="flex flex-col gap-6">
        <Card>
            <CardHeader>
                <CardTitle>Profile</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
                    <MetadataItem label="Email" value={user.email ?? '—'} />
                    <MetadataItem label="Phone" value={user.phoneNumber ?? '—'} />
                    <MetadataItem
                        label="Providers"
                        value={
                            user.providerIds.length === 0 ? (
                                '—'
                            ) : (
                                <div className="flex flex-wrap gap-1">
                                    {user.providerIds.map((providerId) => (
                                        <Badge key={providerId} variant="outline">
                                            {PROVIDER_LABELS[providerId] ?? providerId}
                                        </Badge>
                                    ))}
                                </div>
                            )
                        }
                    />
                    <MetadataItem
                        label="Created"
                        value={user.createdAt ? formatDateTime(user.createdAt) : '— (pre-backfill)'}
                    />
                    <MetadataItem
                        label="Last login"
                        value={`${formatDateTime(user.lastLoginAt)} (${formatRelative(user.lastLoginAt)})`}
                    />
                    <MetadataItem label="Roles" value={<UserRoles userId={user.id} />} />
                    <MetadataItem label="Email verified" value={user.emailVerified ? 'Yes' : 'No'} />
                    <MetadataItem label="Anonymous" value={user.isAnonymous ? 'Yes' : 'No'} />
                    <MetadataItem label="Timezone" value={user.display?.timezone ?? '—'} />
                </div>
            </CardContent>
        </Card>

        <UserLimitsCard user={user} />

        <Card>
            <CardHeader>
                <CardTitle>Object</CardTitle>
            </CardHeader>
            <CardContent>
                <JsonViewer data={user} />
            </CardContent>
        </Card>
    </div>
);

const UserRoles: FC<{ userId: User['id'] }> = ({ userId }) => {
    const roleEntries = useAsyncAtomValue(userRolesState, []);
    const roles = roleEntries.find((entry) => entry.userId === userId)?.roles ?? {};
    const granted = Object.entries(roles)
        .filter(([, enabled]) => enabled === true)
        .map(([role]) => role);
    if (granted.length === 0) {
        return <span className="text-muted-foreground">None</span>;
    }
    return (
        <div className="flex flex-wrap gap-1">
            {granted.map((role) => (
                <Badge key={role} variant="default">
                    {role.toUpperCase()}
                </Badge>
            ))}
        </div>
    );
};

/**
 * Today's server-authoritative AI usage vs the default caps. Counters live at `user-limits/{userId}`
 * and reset by calendar day — a counter from an earlier day means zero usage today.
 */
const UserLimitsCard: FC<{ user: User }> = ({ user }) => {
    const limits = useAsyncAtomValue(userLimitsState(user.id));
    // Admin-browser-local "today" — close enough for a usage readout (counters roll per calendar day).
    const today = todayLocalDate();

    return (
        <Card>
            <CardHeader>
                <CardTitle>AI usage today</CardTitle>
                <CardDescription>
                    Daily capped actions (server-authoritative counters; caps shown are code defaults).
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
                    {UserLimitKeySchema.options.map((key) => {
                        const counter = limits?.[key];
                        const usedToday = counter?.day === today ? counter.count : 0;
                        return (
                            <MetadataItem key={key} label={key} value={`${usedToday} / ${USER_LIMIT_DEFAULTS[key]}`} />
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
};
